import { CircleCheck, Plus, RefreshCw, Upload, XCircle } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate, useParams } from 'react-router';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { cn } from '@core/utils/cn';
import { SectionHeading } from '../../../components/section-heading';
import { Spinner } from '../../../components/spinner';
import { StatCard } from '../../../components/stat-card';
import { SortingRule, TableComponent } from '../../../components/table';
import {
    AccountDto,
    AccountOperationDto
} from '../../../core/api/WalletClient';
import { I18n } from '../../../core/i18n';
import { links } from '../../../core/router';
import { injectTSDI } from '../../../core/tsdi';
import { BulkCorrectionModal } from './bulk-correction-modal';
import { CreateAccountModal } from './create-account-modal';
import { OperationCorrectionModal } from './operation-correction-modal';
import { OperationDeleteModal } from './operation-reject-modal';
import { useStore } from './store';

const formatBalance = (balance: AccountDto['balances'][string]) =>
    `${balance.value.value}${balance.value.currency.symbol}`;

const AccountStatCard = ({ account }: { account: AccountDto }) => {
    const balances = Object.entries(account.balances);
    const [primaryKey, primary] =
        balances.find(([key]) => key === 'APPROVED') ?? balances[0] ?? [];
    const rest = balances.filter(([key]) => key !== primaryKey);

    return (
        <StatCard
            label={`${account.currency.code} ${account.currency.symbol}`}
            value={
                primary ? (
                    <span className="flex items-center gap-2">
                        <AccountBalanceIcon balanceKey={primaryKey} />
                        {formatBalance(primary)}
                    </span>
                ) : (
                    '-'
                )
            }
            hint={
                rest.length
                    ? rest
                          .map(
                              ([key, balance]) =>
                                  `${key}: ${formatBalance(balance)}`
                          )
                          .join(' · ')
                    : undefined
            }
        />
    );
};

export const UserWallet = observer(() => {
    const {
        wallet,
        selectedAccount,
        setSelectedAccount,
        walletAccountOperations,
        dispatch,
        setSorting,
        searchParams,
        sorting
    } = useStore();
    const { formatDateTime } = injectTSDI(I18n);
    const navigate = useNavigate();
    const { userId } = useParams();
    const openDeleteState = React.useState(false);
    const [selectedOperation, setSelectedOperation] = React.useState<
        AccountOperationDto | undefined
    >(undefined);
    const openCorrectionState = React.useState(false);
    const openBulkCorrectionState = React.useState(false);
    const openCreateAccountState = React.useState(false);

    const handleDelete = async (operation: AccountOperationDto) => {
        setSelectedOperation(operation);
        const [, setOpen] = openDeleteState;
        setOpen(true);
    };
    // Todo(mjur): add sorting
    const onSortingChange = (sorting?: SortingRule) => {
        if (sorting) {
            setSorting(sorting);
        }
    };

    const refresh = async () => {
        void wallet.request();
        void walletAccountOperations.request([selectedAccount, searchParams]);
    };

    const openOperationDetails = (operation: AccountOperationDto) =>
        navigate(
            links.userWalletOperationDetails(userId!, operation.id.toString())
        );

    return (
        <>
            <OperationDeleteModal
                openState={openDeleteState}
                operation={selectedOperation}
                onSuccess={refresh}
            />
            <OperationCorrectionModal
                onSuccess={refresh}
                selectedAccount={selectedAccount}
                openState={openCorrectionState}
            />
            <BulkCorrectionModal
                open={openBulkCorrectionState[0]}
                onModalClose={() => openBulkCorrectionState[1](false)}
                onSuccess={refresh}
            />
            <CreateAccountModal
                userId={userId!}
                openState={openCreateAccountState}
                onSuccess={async () => {
                    void wallet.request();
                }}
            />

            <div className="mb-3 flex items-center justify-between gap-4">
                <SectionHeading title="Accounts" />
                <Button
                    variant="outline"
                    onClick={() => openCreateAccountState[1](true)}
                >
                    <Plus />
                    Create Account
                </Button>
            </div>
            {wallet.match({
                loading: () => (
                    <div className="flex min-h-24 items-center justify-center">
                        <Spinner />
                    </div>
                ),
                error: () => (
                    <Alert>
                        <AlertDescription>
                            This user has no wallet yet.
                        </AlertDescription>
                    </Alert>
                ),
                fetched: (wallet) =>
                    wallet.accounts.length === 0 ? (
                        <Alert>
                            <AlertDescription>
                                This user has no wallet accounts yet.
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            {wallet.accounts.map((account) => (
                                <AccountStatCard
                                    key={account.id}
                                    account={account}
                                />
                            ))}
                        </div>
                    )
            })}

            <div className="mb-3 mt-8 flex flex-wrap items-center justify-between gap-4">
                <SectionHeading title="Operations" />
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={() => {
                            const [__, setOpenCorrectionState] =
                                openCorrectionState;
                            setOpenCorrectionState(true);
                        }}
                    >
                        <Plus />
                        Add correction
                    </Button>
                    <Button
                        variant="outline"
                        className="border-warning/40 text-warning hover:text-warning"
                        onClick={() => {
                            openBulkCorrectionState[1](true);
                        }}
                    >
                        <Upload />
                        Bulk Correction
                    </Button>
                </div>
            </div>
            {wallet.$?.accounts.length > 1 && (
                <div className="mb-4 flex flex-wrap items-center gap-2">
                    <span className="mr-1 shrink-0 text-sm">
                        Accounts Filter:
                    </span>
                    {wallet.$?.accounts.map((account) => (
                        <Badge
                            key={account.id}
                            variant={
                                selectedAccount === account.id
                                    ? 'default'
                                    : 'outline'
                            }
                            className="cursor-pointer"
                            onClick={() => {
                                setSelectedAccount(account.id);
                            }}
                        >
                            {account.currency.code}
                        </Badge>
                    ))}
                </div>
            )}
            <TableComponent<AccountOperationDto[]>
                items={walletAccountOperations}
                tableLayout="auto"
                columns={[
                    {
                        Header: 'Date',
                        id: 'createdDate',
                        accessor: (value) => (
                            <span
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openOperationDetails(value);
                                }}
                                className="cursor-pointer font-semibold text-primary hover:underline"
                            >
                                {formatDateTime(value.timestamp)}
                            </span>
                        ),
                        style: { width: 100 }
                    },
                    {
                        Header: 'Value',
                        id: 'value',
                        accessor: (value) =>
                            `${value.value.value} ${value.value.currency.symbol}`,
                        disableSortBy: true
                    },
                    {
                        Header: 'Money Status',
                        id: 'status',
                        accessor: (value) => (
                            <span className="flex items-center gap-1">
                                <AccountBalanceIcon
                                    balanceKey={value.moneyStatus}
                                />
                                {value.moneyStatus}
                            </span>
                        ),
                        disableSortBy: true
                    },
                    {
                        Header: 'Reverted',
                        id: 'deleted',
                        accessor: (operation) =>
                            operation.deleted ? (
                                <span className="flex items-center gap-1">
                                    <XCircle className="size-4 text-destructive" />
                                    Reverted
                                </span>
                            ) : null,
                        disableSortBy: true
                    },
                    {
                        Header: 'Tenant',
                        id: 'tenant',
                        accessor: (operation) =>
                            operation.context?.tenantId ? (
                                <span>{operation.context?.tenantId}</span>
                            ) : null,
                        disableSortBy: true
                    }
                ]}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                onChangePage={(page) => void dispatch({ type: 'page', page })}
                // Todo(mjur): add sorting
                onSortingChange={onSortingChange}
                sorting={Array.isArray(sorting) ? sorting[0] : sorting}
                contextMenu={[
                    {
                        label: 'Details',
                        action: (value) => {
                            openOperationDetails(value);
                        }
                    },
                    {
                        label: 'Revert',
                        hidden: (value) => value.deleted,
                        action: (value) => {
                            handleDelete(value);
                        }
                    }
                ]}
            />
        </>
    );
});

export function AccountBalanceIcon({
    balanceKey,
    className
}: {
    balanceKey: string;
    className?: string;
}) {
    const classes = cn('size-4 shrink-0', className);
    switch (balanceKey) {
        case 'APPROVED':
            return <CircleCheck className={cn(classes, 'text-success')} />;
        case 'PENDING':
            return <RefreshCw className={cn(classes, 'text-info')} />;
        case 'REJECTED':
            return <XCircle className={cn(classes, 'text-destructive')} />;

        default:
            return <span className="inline-block w-4" />;
    }
}

export default UserWallet;
